import type { AppEdge, AppNode } from './types';

export const MAX_SHARED_FLOW_BYTES = 2_000_000;

export type SharedFlowSnapshot = {
  version: 1;
  revision: number;
  nodes: AppNode[];
  edges: AppEdge[];
};

const stripNodeRuntimeState = (node: AppNode): AppNode => {
  const stripped = {
    ...node,
    selected: undefined,
    dragging: undefined,
    className: undefined,
  } as AppNode;
  if (stripped.type !== 'function') return stripped;
  return {
    ...stripped,
    data: {
      ...stripped.data,
      internalNodes: stripped.data.internalNodes.map(stripNodeRuntimeState),
      internalEdges: stripped.data.internalEdges.map(stripEdgeRuntimeState),
    },
  };
};

const stripEdgeRuntimeState = (edge: AppEdge): AppEdge => ({
  ...edge,
  selected: undefined,
  className: undefined,
});

export const createSharedFlowSnapshot = (
  revision: number,
  nodes: AppNode[],
  edges: AppEdge[],
): SharedFlowSnapshot => ({
  version: 1,
  revision,
  nodes: nodes.map(stripNodeRuntimeState),
  edges: edges.map(stripEdgeRuntimeState),
});

export const parseSharedFlowSnapshot = (raw: string): SharedFlowSnapshot => {
  if (new TextEncoder().encode(raw).byteLength > MAX_SHARED_FLOW_BYTES) {
    throw new Error('共有フローのサイズが上限を超えています');
  }
  const value = JSON.parse(raw) as Partial<SharedFlowSnapshot>;
  if (
    value.version !== 1
    || !Number.isSafeInteger(value.revision)
    || (value.revision ?? -1) < 0
    || !Array.isArray(value.nodes)
    || !Array.isArray(value.edges)
    || value.nodes.length > 500
    || value.edges.length > 1_000
  ) {
    throw new Error('共有フローの形式が不正です');
  }
  if (value.nodes.some((node) => !node || typeof node.id !== 'string' || typeof node.type !== 'string')) {
    throw new Error('共有フローに不正なノードがあります');
  }
  if (value.edges.some((edge) => (
    !edge || typeof edge.id !== 'string' || typeof edge.source !== 'string' || typeof edge.target !== 'string'
  ))) {
    throw new Error('共有フローに不正なエッジがあります');
  }
  return value as SharedFlowSnapshot;
};